import { Layer, Source } from 'react-map-gl';
import { green as carbonGreen, red } from '@carbon/colors';

// Each footprint at the same low opacity, in ONE fill layer, so where they overlap the red stacks
// deeper (AC 6). Green is the region every scenario reaches, solid, with a solid outline (AC 9).
const FOOTPRINT_COLOR = red[60];
const FOOTPRINT_OPACITY = 0.18;
const UNION_COLOR = red[70];
const UNION_DASH = [3, 2];
const COVERED_COLOR = carbonGreen[50];
const COVERED_OPACITY = 0.6;
const COVERED_LINE_COLOR = carbonGreen[60];

interface CoverageLayersProps {
  /** Draw the stack and the frame: something is shaped. */
  drawn: boolean;
  stack: GeoJSON.FeatureCollection;
  /** The common region, drawn green; empty for none. */
  region: GeoJSON.FeatureCollection | null;
  union: GeoJSON.FeatureCollection;
}

/**
 * The coverage map's layers, bottom to top: the red stack (the green cut out of it once
 * confirmed), the green with its solid outline, then the dashed frame of the combined footprint.
 *
 * @param {CoverageLayersProps} props - The stack, the region and the union.
 * @return {JSX.Element} The sources and layers.
 */
export function CoverageLayers({
  drawn,
  stack,
  region,
  union,
}: CoverageLayersProps) {
  return (
    <>
      {drawn && (
        <Source id="case-footprints" type="geojson" data={stack}>
          <Layer
            id="case-footprint-fill"
            type="fill"
            paint={{
              'fill-color': FOOTPRINT_COLOR,
              'fill-opacity': FOOTPRINT_OPACITY,
            }}
          />
        </Source>
      )}
      {region && (
        <Source id="case-common-region" type="geojson" data={region}>
          <Layer
            id="case-common-fill"
            type="fill"
            paint={{
              'fill-color': COVERED_COLOR,
              'fill-opacity': COVERED_OPACITY,
            }}
          />
          <Layer
            id="case-common-line"
            type="line"
            paint={{ 'line-color': COVERED_LINE_COLOR, 'line-width': 2 }}
          />
        </Source>
      )}
      {drawn && (
        <Source id="case-footprint-union" type="geojson" data={union}>
          <Layer
            id="case-footprint-union-line"
            type="line"
            paint={{
              'line-color': UNION_COLOR,
              'line-width': 1.5,
              'line-dasharray': UNION_DASH,
            }}
          />
        </Source>
      )}
    </>
  );
}
